import type { SelectItemType } from "@/components/base/select/select";
import type { AdminCourseListItemDto } from "@/types/api";
import { CourseAudience, CourseLevel } from "@/types/domain";

/**
 * Coerce a generated API field (`number | string`) to a plain `number`.
 * Returns 0 for `null`, `undefined`, `NaN`, or un-parseable strings.
 */
export const toNum = (value: number | string | null | undefined): number => {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Format a GBP minor-unit price (pence) for display, dropping the pence for whole pounds:
 * 2500 -> "£25", 1550 -> "£15.50". Returns an empty string when the price is missing or not a finite number.
 */
export const formatCoursePrice = (
  priceAmountPence: number | string | null | undefined,
  currency: string | null | undefined,
): string => {
  const pence = priceAmountPence == null ? NaN : toNum(priceAmountPence);
  if (!Number.isFinite(pence)) return "";
  const symbol = (currency ?? "GBP").toUpperCase() === "GBP" ? "£" : "";
  const amount = pence % 100 === 0 ? String(pence / 100) : (pence / 100).toFixed(2);
  return `${symbol}${amount}`;
};

// Individual audience flag values (excludes None), ordered per AC5
export const AUDIENCE_FLAGS: CourseAudience[] = [
  CourseAudience.HealthcareWorkers,
  CourseAudience.SocialWorkers,
  CourseAudience.SocialCareWorkers,
  CourseAudience.EducationWorkers,
  CourseAudience.ParentsAndCarers,
  CourseAudience.Other,
];

// Individual audience labels (for flags enum)
export const COURSE_AUDIENCE_LABELS: Record<CourseAudience, string> = {
  [CourseAudience.None]: "Not specified",
  [CourseAudience.HealthcareWorkers]: "Healthcare workers",
  [CourseAudience.SocialWorkers]: "Social workers",
  [CourseAudience.SocialCareWorkers]: "Social care workers",
  [CourseAudience.EducationWorkers]: "Education workers",
  [CourseAudience.ParentsAndCarers]: "Parents and carers",
  [CourseAudience.Other]: "Other",
};

export const COURSE_LEVEL_LABELS: Record<CourseLevel, string> = {
  [CourseLevel.Unknown]: "Not specified",
  [CourseLevel.Introduction]: "Introduction",
  [CourseLevel.Intermediate]: "Intermediate",
  [CourseLevel.Advanced]: "Advanced",
  [CourseLevel.Expert]: "Expert",
};

/** Badge colours for course levels, as the design system pairs them. */
export const COURSE_LEVEL_BADGE_COLOR: Record<CourseLevel, "success" | "brand" | "error" | "gray"> =
  {
    [CourseLevel.Unknown]: "gray",
    [CourseLevel.Introduction]: "success",
    [CourseLevel.Intermediate]: "brand",
    [CourseLevel.Advanced]: "error",
    [CourseLevel.Expert]: "error",
  };

export const courseAudienceOptions: SelectItemType[] = AUDIENCE_FLAGS.map((flag) => ({
  id: String(flag),
  label: COURSE_AUDIENCE_LABELS[flag],
}));

export const courseLevelOptions: SelectItemType[] = Object.entries(COURSE_LEVEL_LABELS)
  .filter(([value]) => Number(value) !== CourseLevel.Unknown)
  .map(([value, label]) => ({
    id: value,
    label,
  }));

// The professions behind each worker audience, in the order the long and summary labels list them.
const AUDIENCE_PROFESSIONS: [CourseAudience, string][] = [
  [CourseAudience.SocialWorkers, "social work"],
  [CourseAudience.SocialCareWorkers, "social care"],
  [CourseAudience.HealthcareWorkers, "health"],
  [CourseAudience.EducationWorkers, "education"],
];

const joinWithAnd = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * Sentence-style audience label, e.g. "Professionals in social work, social care, health and education" or
 * "Professionals in education, parents and carers".
 */
const getLongCourseAudienceLabel = (audience: CourseAudience) => {
  const has = (flag: CourseAudience) => (audience & flag) === flag;
  const professions = AUDIENCE_PROFESSIONS.filter(([flag]) => has(flag)).map(([, name]) => name);
  const groups = [
    ...(professions.length > 0 ? [`professionals in ${joinWithAnd(professions)}`] : []),
    ...(has(CourseAudience.ParentsAndCarers) ? ["parents and carers"] : []),
  ];

  if (groups.length === 0) return has(CourseAudience.Other) ? "Other" : "—";

  const label = groups.join(", ") + (has(CourseAudience.Other) ? " and others" : "");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const ALL_PROFESSIONALS = AUDIENCE_PROFESSIONS.reduce<number>((all, [flag]) => all | flag, 0);

/**
 * Compact audience label for the All courses table: one audience's own label ("Healthcare workers"),
 * "All professionals", "Professionals, parents and carers", or sector names joined ("Social work and social care").
 */
const getSummaryCourseAudienceLabel = (audience: CourseAudience) => {
  const flags = getAudienceFlags(audience);
  if (flags.length === 1) return COURSE_AUDIENCE_LABELS[flags[0]];

  const has = (flag: CourseAudience) => (audience & flag) === flag;
  const others = has(CourseAudience.Other) ? " and others" : "";
  const parents = has(CourseAudience.ParentsAndCarers);

  if ((audience & ALL_PROFESSIONALS) === ALL_PROFESSIONALS) {
    return (parents ? "Professionals, parents and carers" : "All professionals") + others;
  }

  const professions = AUDIENCE_PROFESSIONS.filter(([flag]) => has(flag)).map(([, name]) => name);
  const groups = [
    ...(professions.length > 0 ? [joinWithAnd(professions)] : []),
    ...(parents ? ["parents and carers"] : []),
  ];
  const label = groups.join(", ") + others;
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Get the label for a course audience (handles flags/multi-select).
 * `short` (default) returns comma-separated labels for multiple audiences; `long` returns a sentence-style label;
 * `summary` returns the compact label the All courses table uses.
 */
export const getCourseAudienceLabel = (
  audience: CourseAudience | null | undefined,
  variant: "short" | "long" | "summary" = "short",
) => {
  if (audience === null || audience === undefined || audience === CourseAudience.None) return "—";
  if (variant === "long") return getLongCourseAudienceLabel(audience);
  if (variant === "summary") return getSummaryCourseAudienceLabel(audience);

  const labels = AUDIENCE_FLAGS.filter((flag) => (audience & flag) === flag).map(
    (flag) => COURSE_AUDIENCE_LABELS[flag],
  );

  return labels.length > 0 ? labels.join(", ") : "—";
};

/**
 * Get individual audience values from a combined flags value.
 */
export const getAudienceFlags = (audience: CourseAudience | null | undefined): CourseAudience[] => {
  if (audience === null || audience === undefined || audience === CourseAudience.None) return [];

  return AUDIENCE_FLAGS.filter((flag) => (audience & flag) === flag);
};

/**
 * Convert API audience flag names (e.g. `["SocialWorkers", "EducationWorkers"]`) to a combined flags value.
 * Unknown names are ignored.
 */
export const parseAudienceFlagNames = (
  names: readonly string[] | null | undefined,
): CourseAudience =>
  combineAudienceFlags(
    (names ?? [])
      .filter((name): name is keyof typeof CourseAudience =>
        Object.prototype.hasOwnProperty.call(CourseAudience, name),
      )
      .map((name) => CourseAudience[name]),
  );

/**
 * Combine multiple audience values into a single flags value.
 */
export const combineAudienceFlags = (audiences: CourseAudience[]): CourseAudience => {
  if (audiences.length === 0) return CourseAudience.None;
  return audiences.reduce<number>((acc, curr) => acc | curr, 0) as CourseAudience;
};

/** Convert an API level name (e.g. "Introduction") to a {@link CourseLevel}; unknown names map to Unknown. */
export const parseCourseLevelName = (name: string | null | undefined): CourseLevel =>
  name && Object.prototype.hasOwnProperty.call(CourseLevel, name)
    ? CourseLevel[name as keyof typeof CourseLevel]
    : CourseLevel.Unknown;

/** Split a course's newline-separated learning outcomes into trimmed, non-empty lines. */
export const splitLearningOutcomes = (learningOutcomes: string | null | undefined): string[] =>
  (learningOutcomes ?? "")
    .split("\n")
    .map((outcome) => outcome.trim())
    .filter((outcome) => outcome.length > 0);

export const getCourseLevelLabel = (level: CourseLevel | null | undefined) => {
  if (level === null || level === undefined) return "—";
  return COURSE_LEVEL_LABELS[level] ?? COURSE_LEVEL_LABELS[CourseLevel.Unknown];
};

export type CourseStatusTone = "gray" | "success" | "warning" | "brand";

export const getCourseStatusSummary = (
  course: Pick<AdminCourseListItemDto, "isArchived" | "isPublished" | "publishedVersionCount"> &
    Partial<Pick<AdminCourseListItemDto, "isRestricted">>,
): { label: string; tone: CourseStatusTone } => {
  if (course.isArchived) {
    return { label: "Archived", tone: "gray" };
  }

  // Published for the people given access only (LMS-135).
  if (course.isPublished && course.isRestricted) {
    return { label: "Restricted", tone: "brand" };
  }

  if (course.isPublished) {
    return { label: "Published", tone: "success" };
  }

  if (toNum(course.publishedVersionCount) > 0) {
    return { label: "Ready to publish", tone: "warning" };
  }

  return { label: "Draft", tone: "gray" };
};
